import { Reveal } from "./Reveal";
import { Disclosure } from "./Disclosure";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "./SectionHeading";

type Session = {
  n: string;
  name: string;
  goal: string;
  builds: string[];
  deliverable: string;
  tools: string;
};

const sessions: Session[] = [
  {
    n: "01",
    name: "O mapa",
    goal: "Antes de instalar qualquer coisa, a gente senta junto e mapeia a sua operação — ou a área onde o Claude Code vai trabalhar firme.",
    builds: [
      "O que se repete toda semana e toma o seu tempo",
      "Onde a operação trava e onde o dinheiro escapa",
      "O que vai para a IA primeiro — e o que continua com você",
    ],
    deliverable:
      "O mapa da sua operação, com os processos que vão para a IA.",
    tools: "Mapa da operação.",
  },
  {
    n: "02",
    name: "A instalação",
    goal: "O Claude Code instalado no seu computador, junto com você, sem precisar programar.",
    builds: [
      "A estrutura de pastas da empresa e o CLAUDE.md, o manual que o agente lê sempre",
      "A memória da empresa: quem você é, o que vende, como trabalha",
      "GitHub guardando tudo na nuvem e Obsidian como segundo cérebro",
    ],
    deliverable: "O Claude Code rodando na sua máquina, com a sua empresa dentro.",
    tools: "Claude Code, GitHub, Obsidian.",
  },
  {
    n: "03",
    name: "Agentes e skills",
    goal: "Os agentes e as skills que a sua operação pede, criados para a sua necessidade.",
    builds: [
      "Conectores com o que você já usa: Gmail, Agenda, Drive, CRM",
      "Skills com o jeito da sua empresa, escrito uma vez e usado sempre",
      "Sub-agents e hooks: cada agente com a sua função e as suas regras",
    ],
    deliverable:
      "Os primeiros agentes trabalhando nos processos do mapa.",
    tools: "Gmail, Agenda, Drive, CRM.",
  },
  {
    n: "04",
    name: "Seu time de IA",
    goal: "Parar de operar a IA e passar a coordenar uma equipe dela.",
    builds: [
      "O loop agêntico na prática: o agente pensa, planeja e executa",
      "Rotinas agendadas e plugins rodando sem você no meio",
      "Você criando o próximo agente sozinho — um site, uma ferramenta, um sistema",
    ],
    deliverable:
      "Um processo inteiro da sua empresa rodando sem você no meio.",
    tools: "Agentes, rotinas, plugins.",
  },
];

export function SessionsSection() {
  return (
    <section id="sessoes" className="relative py-12 md:py-16">
      <div className="absolute inset-x-0 top-0 gold-divider" />
      <div className="container-narrow">
        <SectionHeading
          eyebrow="O programa"
          title="4 sessões de 1h30. Cada uma termina com algo funcionando."
          subtitle="Não é curso, não é turma, não é gravação. É construção ao vivo, junto com você, no seu computador."
        />

        <div className="relative mx-auto mt-10 max-w-4xl">
          <span
            aria-hidden="true"
            className="absolute left-[27px] top-4 hidden h-[calc(100%-2rem)] w-px bg-gradient-to-b from-gold/60 via-gold/25 to-transparent md:block"
          />

          <div className="space-y-4">
            {sessions.map((s, i) => (
              <Reveal as="article" y={24} duration={0.55} delay={i * 0.06}
                key={s.n}
                className="relative md:pl-20"
              >
                <span
                  className="absolute left-0 top-6 hidden h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-midnight text-xl text-gold md:flex"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {s.n}
                </span>

                <div className="glass-card rounded-2xl p-6 transition-colors hover:border-gold/40 md:p-7">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gold">
                    Sessão {s.n} · 1h30
                  </p>
                  <h3 className="mt-2 text-2xl text-ivory md:text-3xl">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-pretty leading-relaxed text-slate-muted">
                    {s.goal}
                  </p>

                  <ul className="mt-4 space-y-2">
                    {s.builds.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[15px] leading-snug text-ivory/90"
                      >
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <p className="mt-5 rounded-xl border border-gold/25 bg-midnight/50 p-4 text-sm text-gold-soft">
                    <span className="uppercase tracking-[0.2em] text-[10px] text-gold">
                      Entregável
                    </span>
                    <br />
                    {s.deliverable}
                  </p>

                  <Disclosure
                    className="mt-3 border-none"
                    summaryClassName="py-3 text-sm text-slate-muted hover:no-underline"
                    contentClassName="text-sm leading-relaxed text-slate-muted"
                    summary="O que você vai ligar na prática"
                  >
                    {s.tools}
                  </Disclosure>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center gap-3">
          <Button
            asChild
            size="lg"
            className="h-14 bg-gradient-gold px-10 text-base text-primary-foreground hover:opacity-90"
          >
            <a href="#inscricao">Quero meu Sprint Code</a>
          </Button>
          <p className="text-xs text-slate-muted">
            As 4 sessões são agendadas com você — na sua agenda, no seu ritmo.
          </p>
        </div>
      </div>
    </section>
  );
}
